import { Workflow, Users, TrendingUp, Target, Play, Pause, Plus } from "lucide-react";
import Drawer from "../../../components/ui/Drawer";
import Badge from "../../../components/ui/Badge";
import AutomationFlow from "./automation-flow";
import { AUTOMATION_STATUS } from "./automation.data";
import { useAlert } from "../../../../hooks/useAlert";

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl border border-border bg-surface_alt/40 p-3">
      <div className="flex items-center gap-1.5 text-text_muted mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[11px] font-medium">{label}</span>
      </div>
      <div className="text-lg font-bold text-text_primary leading-none">{value}</div>
    </div>
  );
}

export default function AutomationFlowDrawer({ automation: a, open, onClose, onToggle }) {
  const { showAlert } = useAlert();
  if (!a) return null;
  const st = AUTOMATION_STATUS[a.status] ?? AUTOMATION_STATUS.draft;
  const isActive = a.status === "active";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={a.name}
      subtitle={a.goal}
      icon={Workflow}
      width={600}
      footer={
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => onToggle(a)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border border-border text-text_secondary hover:bg-surface_alt transition-colors"
          >
            {isActive ? <><Pause className="w-4 h-4" /> Pause flow</> : <><Play className="w-4 h-4" /> Activate</>}
          </button>
          <button
            onClick={() => showAlert({ type: "success", message: `Changes to "${a.name}" saved.` })}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-white"
            style={{ background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)" }}
          >
            Save flow
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={st.tone} dot>{st.label}</Badge>
          <Badge tone="primary">{a.steps.length} steps</Badge>
          <Badge tone="neutral">Updated {a.updatedAt}</Badge>
        </div>

        {/* Performance */}
        <div className="grid grid-cols-3 gap-3">
          <Stat icon={Users} label="Enrolled" value={a.enrolled.toLocaleString()} color="var(--color-primary)" />
          <Stat icon={TrendingUp} label="Conversion" value={`${a.convRate}%`} color="var(--color-success)" />
          <Stat icon={Target} label="Revenue" value={a.revenue ? `$${a.revenue.toLocaleString()}` : "$0"} color="var(--color-secondary)" />
        </div>

        {/* Live counters */}
        <div className="flex items-center gap-4 rounded-xl border border-border bg-surface p-4">
          <div className="flex-1 text-center">
            <div className="text-xl font-bold text-text_primary leading-none">{a.active.toLocaleString()}</div>
            <div className="text-[11px] text-text_muted mt-1">Currently in flow</div>
          </div>
          <div className="w-px self-stretch" style={{ background: "var(--color-border)" }} />
          <div className="flex-1 text-center">
            <div className="text-xl font-bold text-text_primary leading-none">{a.completed.toLocaleString()}</div>
            <div className="text-[11px] text-text_muted mt-1">Completed</div>
          </div>
        </div>

        {/* Flow builder */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-text_primary">Journey steps</h4>
            <button className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-80 transition-opacity">
              <Plus className="w-3.5 h-3.5" /> Add step
            </button>
          </div>
          <AutomationFlow steps={a.steps} editable />
        </div>
      </div>
    </Drawer>
  );
}
